import { useState, useCallback } from 'react';

export interface ConfiguracionAlerta {
  tipo: 'success' | 'error' | 'confirmation' | 'warning' | 'info';
  titulo: string;
  mensaje: string;
  textoConfirmar?: string;
  textoCancelar?: string;
  autoClose?: boolean;
  duracion?: number;
}

export interface EstadoAlerta extends ConfiguracionAlerta {
  mostrar: boolean;
  onConfirmar?: () => void;
  onCancelar?: () => void;
}

export const useAlertas = () => {
  const [alerta, setAlerta] = useState<EstadoAlerta | null>(null);

  const mostrarAlerta = useCallback((config: ConfiguracionAlerta) => {
    setAlerta({
      ...config,
      mostrar: true,
    });
  }, []);

  const mostrarConfirmacion = useCallback((
    config: Omit<ConfiguracionAlerta, 'tipo'>,
    onConfirmar: () => void,
    onCancelar?: () => void
  ) => {
    setAlerta({
      ...config,
      tipo: 'confirmation',
      mostrar: true,
      onConfirmar: () => {
        onConfirmar();
        cerrarAlerta();
      },
      onCancelar: onCancelar ? () => {
        onCancelar();
        cerrarAlerta();
      } : undefined,
    });
  }, []);

  const mostrarExito = useCallback((titulo: string, mensaje: string, autoClose = true) => {
    mostrarAlerta({
      tipo: 'success',
      titulo,
      mensaje,
      autoClose,
      duracion: 4000,
    });
  }, [mostrarAlerta]);

  const mostrarError = useCallback((titulo: string, mensaje: string) => {
    mostrarAlerta({
      tipo: 'error',
      titulo,
      mensaje,
      autoClose: false,
    });
  }, [mostrarAlerta]);

  const mostrarAdvertencia = useCallback((titulo: string, mensaje: string) => {
    mostrarAlerta({
      tipo: 'warning',
      titulo,
      mensaje,
      autoClose: false,
    });
  }, [mostrarAlerta]);

  const mostrarInfo = useCallback((titulo: string, mensaje: string, autoClose = true) => {
    mostrarAlerta({
      tipo: 'info',
      titulo,
      mensaje,
      autoClose,
      duracion: 3000,
    });
  }, [mostrarAlerta]);

  const cerrarAlerta = useCallback(() => {
    setAlerta(prev => prev ? { ...prev, mostrar: false } : null);
    setTimeout(() => setAlerta(null), 300);
  }, []);

  return {
    alerta,
    mostrarAlerta,
    mostrarConfirmacion,
    mostrarExito,
    mostrarError,
    mostrarAdvertencia,
    mostrarInfo,
    cerrarAlerta,
  };
};

export default useAlertas;